// Date and number formatting, same wording as the prototype (fDay, fTime, ...).
// Everything is in the viewer's time zone; the lead's own time zone is shown
// separately with <LocalTime>.

const pad = (n: number) => String(n).padStart(2, '0');
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function startOfDay(d: Date): number {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

export function dayDiff(iso: string, now = new Date()): number {
  return Math.round((startOfDay(new Date(iso)) - startOfDay(now)) / 864e5);
}

export function fDay(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const dd = dayDiff(iso, now);
  if (dd === 0) return 'Today';
  if (dd === 1) return 'Tomorrow';
  if (dd === -1) return 'Yesterday';
  if (dd > 1 && dd < 7) return DOW[d.getDay()]!;
  return `${d.getDate()} ${MON[d.getMonth()]}`;
}

export function fTime(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const fDT = (iso: string) => `${fDay(iso)}, ${fTime(iso)}`;
export const fShort = (iso: string) => `${fDay(iso)} ${fTime(iso)}`;

export function fLong(iso: string): string {
  const d = new Date(iso);
  return `${DOW[d.getDay()]} ${d.getDate()} ${MON[d.getMonth()]} at ${fTime(iso)}`;
}

/** Value for <input type="datetime-local">. */
// Missing or malformed dates (a call with no due on its next step, a half
// typed input) become an empty field, never a thrown RangeError.
const valid = (d: Date) => !Number.isNaN(d.getTime());

export function toDT(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!valid(d)) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const toD = (iso: string | null | undefined) => toDT(iso).slice(0, 10);

export function fromDT(v: string): string | null {
  const d = v ? new Date(v) : null;
  return d && valid(d) ? d.toISOString() : null;
}

export function fromD(v: string): string | null {
  return v ? fromDT(`${v}T17:00`) : null;
}

/** The prototype's at(days, h, m): a date relative to today, local time. */
export function at(days: number, h = 17, m = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

export const num = (n: number) => n.toLocaleString('en-US');
export const pct = (a: number, b: number) => (b ? `${((a / b) * 100).toFixed(1)}%` : '0%');

export function greeting(now = new Date()): string {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

export const initials = (name: string) =>
  name
    .replace(/^Dr\.\s*/, '')
    .split(' ')
    .map((p) => p[0] ?? '')
    .join('')
    .slice(0, 2);
